/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ArchiveStatus } from "../models/ArchiveStatus";
import type { AttentionDismissalCreate } from "../models/AttentionDismissalCreate";
import type { AttentionDismissalResponse } from "../models/AttentionDismissalResponse";
import type { BoardBadges } from "../models/BoardBadges";
import type { DeleteResponse } from "../models/DeleteResponse";
import type { DeletionCheck } from "../models/DeletionCheck";
import type { MeetingRequest } from "../models/MeetingRequest";
import type { PeopleRecipientsRequest } from "../models/PeopleRecipientsRequest";
import type { PeopleRecipientsResponse } from "../models/PeopleRecipientsResponse";
import type { PeopleSearchRequest } from "../models/PeopleSearchRequest";
import type { PeopleSearchResponse } from "../models/PeopleSearchResponse";
import type { PersonKind } from "../models/PersonKind";
import type { PersonRef } from "../models/PersonRef";
import type { RecentKind } from "../models/RecentKind";
import type { RecentPerson } from "../models/RecentPerson";
import type { TaskDismissalCreate } from "../models/TaskDismissalCreate";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class PeopleService {
  /**
   * Get Session Requests
   * One session's open meeting requests, one per tutor still to answer, for
   * the admin dashboard's ?request= link and the bell. Empty once answered.
   * @returns MeetingRequest Successful Response
   * @throws ApiError
   */
  public static getSessionRequests({
    orgId,
    sessionId,
  }: {
    orgId: number;
    sessionId: number;
  }): CancelablePromise<Array<MeetingRequest>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/people/organizations/{org_id}/requests/{session_id}",
      path: {
        org_id: orgId,
        session_id: sessionId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Search People
   * One page of an organization's people for a tab and an optional search,
   * with the count of every tab.
   * @returns PeopleSearchResponse Successful Response
   * @throws ApiError
   */
  public static searchPeople({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: PeopleSearchRequest;
  }): CancelablePromise<PeopleSearchResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/search",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Board Badges
   * A CRM board's card chips, in one request for every card: each person's
   * pending tasks, Needs attention items, onboarding steps to do and the
   * messages they sent that are unread for this admin, with their People
   * panel row. Only people with something are returned.
   * @returns BoardBadges Successful Response
   * @throws ApiError
   */
  public static getBoardBadges({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: Array<PersonRef>;
  }): CancelablePromise<Array<BoardBadges>> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/board-badges",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get People Recipients
   * Everyone on a tab for an optional search, for Message all.
   * @returns PeopleRecipientsResponse Successful Response
   * @throws ApiError
   */
  public static getPeopleRecipients({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: PeopleRecipientsRequest;
  }): CancelablePromise<PeopleRecipientsResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/recipients",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Dismiss Attention
   * Hide one of a person's issues for every admin of the organization
   * until the issue changes.
   * @returns AttentionDismissalResponse Successful Response
   * @throws ApiError
   */
  public static dismissAttention({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: AttentionDismissalCreate;
  }): CancelablePromise<AttentionDismissalResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/dismissals",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Dismiss Tasks
   * Dismiss tasks due in the Needs attention tab for every admin of the
   * organization, together: a person's Task due (each of their tasks due) or
   * a task's own row. Each comes back if its due date changes. The tasks
   * themselves are untouched.
   * @returns AttentionDismissalResponse Successful Response
   * @throws ApiError
   */
  public static dismissTasks({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: Array<TaskDismissalCreate>;
  }): CancelablePromise<AttentionDismissalResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/dismissals/tasks",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Dismiss Onboarding
   * Take a person out of the Onboarding tab for every admin of the
   * organization, for good (even when their steps are not all done).
   * @returns AttentionDismissalResponse Successful Response
   * @throws ApiError
   */
  public static dismissOnboarding({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: PersonRef;
  }): CancelablePromise<AttentionDismissalResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/onboarding/dismissals",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Archive Person
   * Leave a person out of the People panel, the meeting pickers and
   * group messages. Nothing else about them changes.
   * @returns ArchiveStatus Successful Response
   * @throws ApiError
   */
  public static archivePerson({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: PersonRef;
  }): CancelablePromise<ArchiveStatus> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/archive",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Restore Person
   * Bring an archived person back.
   * @returns ArchiveStatus Successful Response
   * @throws ApiError
   */
  public static restorePerson({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: PersonRef;
  }): CancelablePromise<ArchiveStatus> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/restore",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Deletion Check
   * Whether one of the organization's students, parents or tutors can be
   * deleted for good: only someone with no history (meetings, money,
   * messages, notes, form responses, tasks, bell notifications, a login, or a
   * card or bank on file). Says why not, or what goes with them.
   * @returns DeletionCheck Successful Response
   * @throws ApiError
   */
  public static getDeletionCheck({
    orgId,
    kind,
    personId,
  }: {
    orgId: number;
    kind: PersonKind;
    personId: number;
  }): CancelablePromise<DeletionCheck> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/people/organizations/{org_id}/deletable",
      path: {
        org_id: orgId,
      },
      query: {
        kind: kind,
        person_id: personId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Person
   * Delete one of the organization's students, parents or tutors for good,
   * with their CRM values, links, onboarding entry and other setup, if they
   * still have no history. If history appeared since the check, it refuses
   * with the reason. Nothing is deleted in Stripe or Auth0.
   * @returns DeleteResponse Successful Response
   * @throws ApiError
   */
  public static deletePerson({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: PersonRef;
  }): CancelablePromise<DeleteResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/people/organizations/{org_id}/delete",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Recent People
   * The people pickers' Recent group for the signed-in admin: the people of
   * these kinds they dealt with lately (messages and forms they sent, tasks
   * assigned to them), newest first, at most `limit`. Then the students,
   * parents and tutors whose profile pages their browser says they opened
   * (visited_*), named, if not listed already. Only the organization's
   * people who are not archived, and its other admins. Every kind when
   * `kinds` is left out.
   * @returns RecentPerson Successful Response
   * @throws ApiError
   */
  public static getRecentPeople({
    orgId,
    kinds,
    limit = 8,
    visitedStudents,
    visitedParents,
    visitedTutors,
  }: {
    orgId: number;
    kinds?: Array<RecentKind> | null;
    limit?: number;
    visitedStudents?: Array<number> | null;
    visitedParents?: Array<number> | null;
    visitedTutors?: Array<number> | null;
  }): CancelablePromise<Array<RecentPerson>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/people/organizations/{org_id}/recent",
      path: {
        org_id: orgId,
      },
      query: {
        kinds: kinds,
        limit: limit,
        visited_students: visitedStudents,
        visited_parents: visitedParents,
        visited_tutors: visitedTutors,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
